import { FC, useCallback, useMemo, useState } from 'react';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import {
	compareStrings,
	getCurrency,
	getWorkOrderTimeZoneId,
	nullSafeGet,
} from '../../utils/DataAccessUtils';
import { Button, Card, Col, Divider, message, Row } from 'antd';
import { UserName } from '../name_component/user/UserNameComponent';
import moment from 'moment';
import InvoiceActions from '../common/InvoiceActions';
import InvoicesAdditionalDetails from '../invoices_components/InvoicesAdditionalDetails';
import { BLACKS_AND_WHITES, optionalFilter } from '../../utils/DataFormatterUtils';
import * as React from 'react';
import InvoiceDraftBanner from '../invoice_draft_banner/InvoiceDraftBanner';
import { InvoiceEnrichmentBanner } from '../invoice_enrichment_banner/InvoiceEnrichmentBanner';
import {
	getHydratedSuggestedAsset,
	getInvoiceAssetSuggestion,
	isAssetLinkPending,
	isAssetSuggestionOfferable,
} from '../../utils/InvoiceUtils';
import { isInternalTech } from '../../utils/AuthUtils';
import { isInvoiceWorkOrderCheckEnabled } from '../../utils/FeatureFlagUtils';
import {
	downloadInvoicePDF,
	updateInvoiceGLCodeForBuyer,
	updateInvoiceGLCodeForSupplier,
	updateInvoicePONumberForBuyer,
	updateInvoicePONumberForSupplier,
} from '../../thunks/invoices_thunks';
import {
	appendWorkOrderNotesForBuyer,
	appendWorkOrderNotesForSupplier,
} from '../../thunks/work_orders_thunks';
import InvoiceUploadButton from '../invoice_upload_button/InvoiceUploadButton';
import { ROLE_TYPES } from '../../utils/DataConstants';
import DateTimeHover from '../date_time_component/DateTime';
import EditGLCodeForm from '../edit_gl_code_form/EditGLCodeForm';
import EditPONumberForm from '../edit_po_number_form/EditPONumberForm';
import InvoiceEditButton from '../invoice_edit_button/InvoiceEditButton';
import InvoiceDetailsDisplay from './InvoiceDetailsDisplay';
import {
	getInvoiceSubUrl,
	getInvoiceRestCrudThunksContractorId,
	isClientInvoiceView,
	getLastApprovedOrDisputedRevision,
} from '../invoice_utilities/InvoiceUtilities';
import { INVOICE_STATUSES } from '../../constants/InvoiceStatuses';

const style = require('./InvoicePageDisplay.less');

const Wrapper = ({ children, invoice, cardWrapper }) =>
	cardWrapper ? (
		<Row
			style={{ margin: '16px 8px' }}
			gutter={12}
			key={invoice.id}
			className={'invoicePageDisplay'}
		>
			<Col span={24}>
				<Card>{children}</Card>
			</Col>
		</Row>
	) : (
		<span className="invoicePageDisplay">{children}</span>
	);

const InvoicePageDisplay: FC<any> = ({
	invoice,
	refreshInvoice,
	showViewInvoicePage = false,
	invoicesUpdating = false,
	isClientView = false,

	currentUser,
	history,
	userType,

	updateInvoice,
	appendWorkOrderNotes,
	updatePONumber,
	updateGLCode,
	updateContractorInvoice,

	setAddingMarkup,
	setEditingInvoice,
	setCopyInvoice,
	otherInvoice = {},
	otherInvoiceLoading = false,
	isDisplayOnly = false,
	cardWrapper = true,
	displayComparison,
	// Opt-in: this component is also rendered per row by the revisions table, where a superseded
	// revision must never offer an asset write. The invoice detail screens and the work order
	// invoices tabs opt in; on a tab each invoice card offers what its own invoice printed.
	showEnrichmentBanner = false,
	// The work order as the page holds it now, for the enrichment banner only. A list invoice's
	// own `workOrder` is a snapshot from the fetch, so after one card links an asset the others
	// would keep offering the stale link; the live record keeps every card on the same answer.
	currentWorkOrder = undefined,
}) => {
	const [generatingPDF, setGeneratingPDF] = useState(false);
	const [editPONumberFormVisible, setEditPONumberFormVisible] = useState(false);
	const [editGLCodeFormVisible, setEditGLCodeFormVisible] = useState(false);
	const formRefs = {
		editGLCodeForm: null,
		editPONumberForm: null,
	};
	const saveFormRef = (formName) => (formRef) => {
		formRefs[formName] = formRef;
	};
	const lastInvoice = getLastApprovedOrDisputedRevision(invoice);
	const showComparison = compareStrings(nullSafeGet('status', invoice), INVOICE_STATUSES.pending)
		? lastInvoice && displayComparison
		: false;

	const isDraft = compareStrings(invoice.status, INVOICE_STATUSES.draft);
	const isBuyerOrInternalTech = useMemo(
		() => userType === ROLE_TYPES.BUYER || isInternalTech(currentUser),
		[userType, currentUser]
	);
	const workOrder = useMemo(() => nullSafeGet('workOrder', invoice), [invoice]);
	const enrichmentInvoice = useMemo(
		() => (currentWorkOrder ? { ...invoice, workOrder: currentWorkOrder } : invoice),
		[invoice, currentWorkOrder]
	);
	const hasContractorId = useMemo(() => nullSafeGet('contractorWorkOrderId', invoice), [invoice]);
	const invoiceId = useMemo(() => nullSafeGet('id', invoice), [invoice]);
	const isBuyerInvoice = useMemo(() => nullSafeGet('isBuyerUploaded', invoice) || false, [invoice]);
	// A project invoice has no work order, so a work-order-only lookup fell back to USD
	// and rendered every labor, material, tax and total figure in USD while the row and
	// the page header — which both read the supplier facility — showed the real currency.
	const currency = useMemo(
		() =>
			getCurrency({
				workOrder: nullSafeGet('workOrder', invoice),
				supplierFacility: nullSafeGet('supplierFacility', invoice),
			}),
		[invoice]
	);

	const generateInvoicePDF = useCallback(
		(id) => {
			setGeneratingPDF(true);
			downloadInvoicePDF(id, userType, currentUser, invoice)
				.catch(() => {
					message.error('An error occurred in downloading the invoice. Please try again!');
				})
				.finally(() => {
					setGeneratingPDF(false);
				});
		},
		[downloadInvoicePDF, userType, currentUser, invoice]
	);

	const handleInvoicePDFUploadChange = useCallback(
		(invoice) => (invoiceFileUrl) => {
			const newInvoice = {
				...invoice,
				invoicePDFLink: invoiceFileUrl,
			};
			updateInvoice(newInvoice, hasContractorId)
				.catch(() => {
					message.error('An error occurred in uploading the invoice. Please try again!');
				})
				.then(() => {
					refreshInvoice();
				});
		},
		[updateInvoice, refreshInvoice]
	);

	const handleEditPONumberSubmit = useCallback(() => {
		const form = formRefs['editPONumberForm'].props.form;
		form.validateFields((err, values) => {
			if (err) {
				return;
			}
			const poNumber = {
				...values,
			};
			updatePONumber(poNumber)
				.then(() => {
					setEditPONumberFormVisible(false);
					form.resetFields();
				})
				.catch(() => {
					message.error('An error occurred updating the PO Number. Please try again!');
				});
		});
	}, [formRefs, updatePONumber]);

	const handleEditGLCodeSubmit = useCallback(() => {
		const form = formRefs['editGLCodeForm'].props.form;
		form.validateFields((err, values) => {
			if (err) {
				return;
			}

			const glCode = {
				...values,
			};

			updateGLCode(glCode)
				.then(() => {
					setEditGLCodeFormVisible(false);
					form.resetFields();
				})
				.catch(() => {
					message.error('An error occurred updating the GL Code. Please try again!');
				});
		});
	}, [formRefs, updateGLCode]);

	const buyerAndInternalSupplierPassThroughItems = useMemo(
		() => [
			...(nullSafeGet('workOrder.lastServiceCall.checkOutTime', invoice)
				? [
						{
							key: 'Service Completed On',
							value: (
								<DateTimeHover
									timestamp={nullSafeGet('workOrder.lastServiceCall.checkOutTime', invoice)}
									showDate={true}
									showTime={false}
									timeZone={getWorkOrderTimeZoneId(nullSafeGet('workOrder', invoice))}
								/>
							),
						},
				  ]
				: []),
			{
				key: 'GL Code',
				value: (
					<div>
						{optionalFilter(nullSafeGet('glCode', invoice))}
						{!isDisplayOnly && isBuyerOrInternalTech ? (
							<Button onClick={() => setEditGLCodeFormVisible(true)} size="large" type="link">
								<i className="icons8-font icons8-edit">Edit</i>
							</Button>
						) : null}
					</div>
				),
			},
			{
				key: 'PO Number',
				value: (
					<div>
						{optionalFilter(nullSafeGet('poNumber', invoice))}
						{!isDisplayOnly && isBuyerOrInternalTech ? (
							<Button onClick={() => setEditPONumberFormVisible(true)} size="large" type="link">
								<i className="icons8-font icons8-edit">Edit</i>
							</Button>
						) : null}
					</div>
				),
			},
			nullSafeGet('approvedByContact', invoice)
				? {
						key: 'Approved By',
						value: (
							<UserName
								contact={nullSafeGet('approvedByContact', invoice)}
								mode={'inline'}
								placement={'top'}
							/>
						),
				  }
				: {},
			nullSafeGet('approvedAt', invoice)
				? {
						key: 'Approved On',
						value: (
							<DateTimeHover
								timestamp={nullSafeGet('approvedAt', invoice)}
								showDate={true}
								showTime={false}
								timeZone={getWorkOrderTimeZoneId(nullSafeGet('workOrder', invoice))}
							/>
						),
				  }
				: {},
			nullSafeGet('processedByContact', invoice)
				? {
						key: 'Marked Processing By',
						value: (
							<UserName
								contact={nullSafeGet('processedByContact', invoice)}
								mode={'inline'}
								placement={'top'}
							/>
						),
				  }
				: {},
			nullSafeGet('processedAt', invoice)
				? {
						key: 'Marked Processing On',
						value: (
							<DateTimeHover
								timestamp={nullSafeGet('processedAt', invoice)}
								showDate={true}
								showTime={false}
								timeZone={getWorkOrderTimeZoneId(nullSafeGet('workOrder', invoice))}
							/>
						),
				  }
				: {},
			nullSafeGet('markedPaidByContact', invoice)
				? {
						key: 'Marked Paid By',
						value: (
							<UserName
								contact={nullSafeGet('markedPaidByContact', invoice)}
								mode={'inline'}
								placement={'top'}
							/>
						),
				  }
				: {},
			nullSafeGet('markedPaidAt', invoice)
				? {
						key: 'Marked Paid On',
						value: (
							<DateTimeHover
								timestamp={nullSafeGet('markedPaidAt', invoice)}
								showDate={true}
								showTime={false}
								timeZone={getWorkOrderTimeZoneId(nullSafeGet('workOrder', invoice))}
							/>
						),
				  }
				: {},
		],
		[invoice, isDisplayOnly, isBuyerOrInternalTech]
	);

	const lastDisputeNote = useMemo(
		() => nullSafeGet('disputeNotes', invoice) && invoice.disputeNotes.slice(-1)[0],
		[invoice]
	);
	const disputeNote = useMemo(
		() =>
			lastDisputeNote ? (
				<Col>
					<div>
						<span style={{ fontSize: 16 }}>
							Disputed by{' '}
							<UserName
								mode={'inline'}
								contact={nullSafeGet(`noteAddedByContact`, lastDisputeNote)}
							/>{' '}
							{moment(nullSafeGet(`noteAddedAt`, lastDisputeNote)).fromNow()}
						</span>
					</div>
					<div
						style={{
							display: 'inline-flex',
							background: '#fafafa',
							color: 'rgba(0, 0, 0, 0.65)',
							borderRadius: '30px',
							padding: '8px 16px',
						}}
					>
						<span
							style={{
								fontSize: 16,
							}}
						>
							{nullSafeGet(`text`, lastDisputeNote)}
						</span>
					</div>
					{!isBuyerOrInternalTech ? (
						<div className={'mt-2 flex gap-4'}>
							<Button
								type="primary"
								key="Edit"
								onClick={() => history.push(`/${userType}/invoices/edit/${invoiceId}`)}
							>
								Edit Invoice
							</Button>
							{/* Notes live on the work order, so a project invoice has nowhere to send one. */}
							{nullSafeGet('workOrderId', invoice) ? (
								<Button
									key="Note"
									onClick={() =>
										history.push(
											`/${userType}/workOrders/detail/${nullSafeGet('workOrderId', invoice)}/notes`
										)
									}
								>
									Send a note
								</Button>
							) : null}
						</div>
					) : null}
				</Col>
			) : null,
		[history, invoice, invoiceId, isBuyerOrInternalTech, lastDisputeNote, userType]
	);
	const path = getInvoiceSubUrl(userType, currentUser, invoice);

	return (
		<Wrapper invoice={invoice} cardWrapper={cardWrapper}>
			{isBuyerOrInternalTech
				? [
						<EditGLCodeForm
							formData={invoice}
							wrappedComponentRef={saveFormRef('editGLCodeForm')}
							visible={editGLCodeFormVisible}
							confirming={invoicesUpdating}
							onCancel={() => setEditGLCodeFormVisible(false)}
							onSubmit={handleEditGLCodeSubmit}
						/>,
						<EditPONumberForm
							formData={invoice}
							wrappedComponentRef={saveFormRef('editPONumberForm')}
							visible={editPONumberFormVisible}
							confirming={invoicesUpdating}
							onCancel={() => setEditPONumberFormVisible(false)}
							onSubmit={handleEditPONumberSubmit}
						/>,
				  ]
				: null}
			{isDisplayOnly ? null : (
				<>
					{showViewInvoicePage ? (
						<Row justify={'space-between'}>
							<Col />
							<Col>
								<Button
									onClick={() => {
										history.push(`/${path}/detail/${invoice.id}`);
									}}
								>
									View Invoice Page
								</Button>
								<InvoiceEditButton invoice={invoice} />
							</Col>
						</Row>
					) : null}
					<InvoiceActions
						invoice={invoice}
						refreshInvoice={refreshInvoice}
						updateInvoice={updateInvoice}
						style={style}
						appendWorkOrderNotes={appendWorkOrderNotes}
						showEditAndViewButton={false}
						isClientView={isClientView}
						setAddingMarkup={setAddingMarkup}
						setEditingInvoice={setEditingInvoice}
						setCopyInvoice={setCopyInvoice}
						otherInvoiceLoading={otherInvoiceLoading}
						otherInvoice={otherInvoice}
					/>
					<InvoiceDraftBanner
						invoice={invoice}
						generateInvoicePDF={generateInvoicePDF}
						handleInvoicePDFUploadChange={handleInvoicePDFUploadChange}
						generatingPDF={generatingPDF}
					/>
				</>
			)}
			{/* Flag-gated on its own: assetSuggestion is stored on the invoice, so without this an
			    invoice created while the flag was on would keep offering asset writes after it was
			    turned off. */}
			{isInvoiceWorkOrderCheckEnabled() &&
			showEnrichmentBanner &&
			!isDisplayOnly &&
			isAssetSuggestionOfferable(enrichmentInvoice) ? (
				<InvoiceEnrichmentBanner
					assetSuggestion={getInvoiceAssetSuggestion(enrichmentInvoice)}
					hydratedAsset={getHydratedSuggestedAsset(enrichmentInvoice)}
					linkPending={isAssetLinkPending(enrichmentInvoice)}
					workOrderId={nullSafeGet('workOrderId', enrichmentInvoice)}
					workOrder={nullSafeGet('workOrder', enrichmentInvoice)}
				/>
			) : null}
			{showComparison ? (
				<Row style={{ alignItems: 'baseline', marginBottom: 20 }}>
					<h5 style={{ marginBottom: 0 }}>Cost & Scope Breakdown</h5>
					<span
						style={{
							color: BLACKS_AND_WHITES.blackSecondary,
							marginLeft: 12,
						}}
					>
						Changes from the previous invoice are shown below
					</span>
				</Row>
			) : null}
			<InvoicesAdditionalDetails
				invoice={invoice}
				lastInvoice={lastInvoice}
				passthroughItems={[
					<InvoiceUploadButton
						handleInvoicePDFUploadChange={handleInvoicePDFUploadChange}
						invoice={invoice}
						generatingPDF={generatingPDF}
						generateInvoicePDF={generateInvoicePDF}
					/>,
					{
						key: 'File',
						value: (
							<InvoiceUploadButton
								handleInvoicePDFUploadChange={handleInvoicePDFUploadChange}
								invoice={invoice}
								generatingPDF={generatingPDF}
								generateInvoicePDF={generateInvoicePDF}
								isDisplayOnly={isDisplayOnly}
							/>
						),
						className: 'detailRow__item firstRow lastCol',
					},
					...(nullSafeGet('workOrder.plannedMaintenanceSchedule.supplierContractId', invoice)
						? [
								{
									key: 'Contract ID',
									value: nullSafeGet(
										'workOrder.plannedMaintenanceSchedule.supplierContractId',
										invoice
									),
								},
						  ]
						: []),
					...(isBuyerOrInternalTech ? buyerAndInternalSupplierPassThroughItems : []),
				]}
				showComparison={showComparison}
			/>
			<Divider />
			<InvoiceDetailsDisplay
				invoice={invoice}
				lastInvoice={lastInvoice}
				currency={currency}
				displayComparison={showComparison}
			/>
		</Wrapper>
	);
};

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	updateInvoice: (entity, hasContractorId) =>
		dispatch(
			getInvoiceRestCrudThunksContractorId(
				ownProps.userType,
				hasContractorId,
				isClientInvoiceView(entity, ownProps.currentUser)
			).update(entity)
		),
	appendWorkOrderNotes: (entity) =>
		ownProps.userType === ROLE_TYPES.SUPPLIER
			? dispatch(appendWorkOrderNotesForSupplier(entity))
			: dispatch(appendWorkOrderNotesForBuyer(entity)),
	updateGLCode: (entity) =>
		ownProps.userType === ROLE_TYPES.SUPPLIER
			? dispatch(updateInvoiceGLCodeForSupplier(entity))
			: dispatch(updateInvoiceGLCodeForBuyer(entity)),
	updatePONumber: (entity) =>
		ownProps.userType === ROLE_TYPES.SUPPLIER
			? dispatch(updateInvoicePONumberForSupplier(entity))
			: dispatch(updateInvoicePONumberForBuyer(entity)),
});

const ComponentWithoutUserType = withRouter(
	connect(mapStateToProps, mapDispatchToProps)(InvoicePageDisplay)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
		currentUser: (state as any).session.currentUser,
	}),
	() => ({})
)(ComponentWithoutUserType);
